/**
 * The contact icons a CV prints.
 *
 * They live in their own module, and depend on nothing but React and
 * `lucide-react`, so a surface that needs the marks does not have to pull the
 * whole 2,000-line renderer (and its `@tabbio/types` / `@tabbio/utils` graph)
 * in behind them. `apps/partners` draws the same CV as an editable sheet and
 * imports exactly this file; without it that app was reaching for its own icon
 * set, so a LinkedIn link on the partner's page and the same link on the
 * client's share link were two different glyphs.
 *
 * Several of the marks are inline paths rather than `lucide-react` glyphs.
 * LinkedIn, WhatsApp, Telegram, Behance, TikTok and X are BRANDS: their real
 * marks are filled and trademarked shapes, and a 1.75-weight outline stand-in
 * reads as a different company's logo. Everything else is a generic idea (a
 * pin, a phone, a globe) and takes the stroked lucide glyph at the same 13px.
 */

import {
  Dribbble,
  Facebook,
  Github,
  Globe,
  Instagram,
  Mail,
  MapPin,
  Phone,
  Youtube,
} from "lucide-react";

/**
 * Every contact a CV can carry. `WebCvContact["id"]` is this list, so the icon
 * switch and the contact vocabulary cannot drift apart.
 */
export type CvContactIconId =
  | "location"
  | "phone"
  | "email"
  | "website"
  | "linkedin"
  | "github"
  | "x"
  | "instagram"
  | "youtube"
  | "tiktok"
  | "facebook"
  | "behance"
  | "dribbble"
  | "whatsapp"
  | "telegram"
  | "tabbio-message"
  | "tabbio"
  | "link";

export const TABBIO_MARK_PATH =
  "M5.05762 0.0242382C8.24806 -0.0283878 11.5976 0.0210352 14.8027 0.0213085C14.7806 1.07161 14.8009 2.19575 14.8008 3.2508L14.7998 9.52326L21.5635 9.52619L21.5596 14.9774L14.8018 14.9754L14.7998 22.5067L24.0361 22.5096L24.0342 26.2781L24.0322 27.9979L17.8818 27.9998C15.2273 27.9971 13.0641 27.6624 11.0742 25.6922C9.10929 23.7465 9.30806 21.8741 9.30957 19.3055L9.31348 14.9744L0.000976562 14.9754L0 5.05842C1.68873 3.43561 3.35518 1.61203 5.05762 0.0242382ZM5.71582 5.82111L2.00977 5.81818V9.52424L5.62207 9.52521L9.31055 9.52717L9.31348 1.95197L5.71973 1.951L5.71582 5.82111Z";

export function TabbioContactIcon({ color }: { color: string }) {
  return (
    <svg
      width={11}
      height={13}
      viewBox="0 0 24.0361 27.9998"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flexShrink: 0 }}
    >
      <path d={TABBIO_MARK_PATH} fill={color} />
    </svg>
  );
}

// Real brand geometry shared with the other surfaces: Behance from the mobile
// BrandIcons set (16x16), TikTok and X from the public-profile social glyphs.
const BEHANCE_MARK_PATH =
  "M4.654 3c.461 0 .887.035 1.278.14.39.07.711.216.996.391s.497.426.641.747c.14.32.216.711.216 1.137 0 .496-.106.922-.356 1.242-.215.32-.566.606-.997.817.606.176 1.067.496 1.348.922s.461.957.461 1.563c0 .496-.105.922-.285 1.278a2.3 2.3 0 01-.782.887c-.32.215-.711.39-1.137.496a5.3 5.3 0 01-1.278.176L0 12.803V3zm-.285 3.978c.39 0 .71-.105.957-.285.246-.18.355-.497.355-.887 0-.216-.035-.426-.105-.567a1 1 0 00-.32-.355 1.8 1.8 0 00-.461-.176c-.176-.035-.356-.035-.567-.035H2.17v2.31c0-.005 2.2-.005 2.2-.005zm.105 4.193c.215 0 .426-.035.606-.07.176-.035.356-.106.496-.216s.25-.215.356-.39c.07-.176.14-.391.14-.641 0-.496-.14-.852-.426-1.102-.285-.215-.676-.32-1.137-.32H2.17v2.734h2.305zm6.858-.035q.428.427 1.278.426c.39 0 .746-.106 1.032-.286q.426-.32.53-.64h1.74c-.286.851-.712 1.457-1.278 1.848-.566.355-1.243.566-2.06.566a4.1 4.1 0 01-1.527-.285 2.8 2.8 0 01-1.137-.782 2.85 2.85 0 01-.712-1.172c-.175-.461-.25-.957-.25-1.528 0-.531.07-1.032.25-1.493.18-.46.426-.852.747-1.207.32-.32.711-.606 1.137-.782a4 4 0 011.493-.285c.606 0 1.137.105 1.598.355.46.25.817.532 1.102.958.285.39.496.851.641 1.348.07.496.105.996.07 1.563h-5.15c0 .58.21 1.11.496 1.396m2.24-3.732c-.25-.25-.642-.391-1.103-.391-.32 0-.566.07-.781.176s-.356.25-.496.39a.96.96 0 00-.25.497c-.036.175-.07.32-.07.46h3.196c-.07-.526-.25-.882-.497-1.132zm-3.127-3.728h3.978v.957h-3.978z";

const TIKTOK_MARK_PATH =
  "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z";

const X_MARK_PATH =
  "M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z";

export function BrandContactIcon({
  id,
  color,
  size = 13,
}: {
  id: "whatsapp" | "linkedin" | "telegram" | "behance" | "tiktok" | "x";
  color: string;
  size?: number;
}) {
  const path =
    id === "whatsapp"
      ? "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
      : id === "linkedin"
        ? "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
        : id === "behance"
          ? BEHANCE_MARK_PATH
          : id === "tiktok"
            ? TIKTOK_MARK_PATH
            : id === "x"
              ? X_MARK_PATH
              : "M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z";

  return (
    <svg
      width={size}
      height={size}
      viewBox={id === "behance" ? "0 0 16 16" : "0 0 24 24"}
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flexShrink: 0 }}
    >
      <path d={path} fill={color} />
    </svg>
  );
}

export function CvContactIcon({
  id,
  color,
}: {
  id: CvContactIconId;
  color: string;
}) {
  if (id === "location")
    return <MapPin size={13} color={color} strokeWidth={1.75} />;
  if (id === "phone")
    return <Phone size={13} color={color} strokeWidth={1.75} />;
  if (id === "email")
    return <Mail size={13} color={color} strokeWidth={1.75} />;
  if (id === "linkedin")
    return <BrandContactIcon id="linkedin" color={color} />;
  if (id === "whatsapp")
    return <BrandContactIcon id="whatsapp" color={color} />;
  if (id === "telegram")
    return <BrandContactIcon id="telegram" color={color} />;
  if (id === "github")
    return <Github size={13} color={color} strokeWidth={1.75} />;
  if (id === "x") return <BrandContactIcon id="x" color={color} />;
  if (id === "instagram")
    return <Instagram size={13} color={color} strokeWidth={1.75} />;
  if (id === "youtube")
    return <Youtube size={13} color={color} strokeWidth={1.75} />;
  if (id === "tiktok") return <BrandContactIcon id="tiktok" color={color} />;
  if (id === "facebook")
    return <Facebook size={13} color={color} strokeWidth={1.75} />;
  if (id === "dribbble")
    return <Dribbble size={13} color={color} strokeWidth={1.75} />;
  if (id === "behance")
    return <BrandContactIcon id="behance" color={color} />;
  if (id === "tabbio" || id === "tabbio-message")
    return <TabbioContactIcon color={color} />;
  return <Globe size={13} color={color} strokeWidth={1.75} />;
}
